/**
 * document-additional-renderers.tsx — F12S Etap 2 Dokumenty.
 *
 * Fabryki contextRenderers dla zakładek tabel dodatkowych (additional_view /
 * satellite_view) używane przez wszystkie strony detali dokumentów:
 * PurchaseInvoiceDetail, SaleInvoiceDetail, CommercialDocDetail, InternalDocDetail.
 *
 * Wyekstrahowane do osobnego pliku żeby:
 *  - wyeliminować duplikację między stronami dokumentów,
 *  - umożliwić unit-test logiki wiring bez pełnego RTL mount'a strony.
 *
 * Uwaga: importuje React (JSX), stąd rozszerzenie .tsx.
 */

import type { ReactNode } from 'react';
import type { DocumentClass } from '@saas/shared';

import type {
  AdditionalTableAddRowDraft,
  AdditionalTableDeltaSlice,
} from '@/components/additional/AdditionalTableTab';
import { AdditionalTableTab } from '@/components/additional/AdditionalTableTab';
import { AdditionalViewTab } from '@/components/additional/AdditionalViewTab';
import type { LayoutElement } from '@/components/form/RecursiveFormRenderer';
import { buildProcessLayoutRenderers } from '@/components/processes/processLayoutRenderers';
import {
  parseRecordLinksElementConfig,
  RecordLinksSection,
} from '@/components/record-links/RecordLinksSection';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { DOCUMENTS_MODULE_CODE } from '@/lib/processes/module-routes';

/**
 * Opcje fabryki (Krok 6 atomic-save refactor — opcjonalne, backward-compatible;
 * `getBufferedValue` dodane w Kroku 7 — bugfix utraty danych).
 * Gdy pominięte: zachowanie identyczne jak dotąd (AdditionalTableTab w trybie
 * auto-save HTTP, `buffered` domyślnie false).
 */
export interface BuildDocumentAdditionalRenderersOptions {
  /**
   * `false` renderuje tabele dodatkowe strony w trybie tylko-do-odczytu (brak
   * klik-do-edycji, brak triggera pickera reference, brak add-row/usuwania).
   * Default `true` (backward-compatible) — strony przekazują tu swój stan `editing`.
   */
  editable?: boolean;
  /** Włącza tryb buforowany (bez auto-save HTTP) na WSZYSTKICH tabelach dodatkowych strony. */
  buffered?: boolean;
  /** Wywoływane per tabela (tableId) z jej pełnym deltą — tylko gdy `buffered`. */
  onDeltasChange?: (tableId: string, delta: AdditionalTableDeltaSlice) => void;
  /**
   * Getter rozwiązujący CURRENT delta slice dla danej tabeli (Krok 7 bugfix —
   * kontrolowany bufor). Wołany PRZY KAŻDYM renderze zakładki (nie tylko przy
   * mount'cie), więc czyta aktualną wartość z bufora rodzica (np.
   * `additionalDeltas.get(tableId)`), a nie zamrożoną z chwili stworzenia
   * funkcji renderującej. Tylko gdy `buffered`; gdy pominięte —
   * `AdditionalTableTab` używa swojego domyślnego pustego slice'a.
   */
  getBufferedValue?: (tableId: string) => AdditionalTableDeltaSlice | undefined;
  /**
   * Getter rozwiązujący szkic NIEZATWIERDZONEGO wiersza dodawania danej tabeli
   * (Bug 2 — lost-on-tab-switch fix), mode-independent — przekazywany
   * BEZWARUNKOWO (nie tylko gdy `buffered`), analogicznie do `getBufferedValue`.
   */
  getAddRowDraft?: (tableId: string) => AdditionalTableAddRowDraft | undefined;
  /** Wywoływane przy każdej zmianie szkicu add-row danej tabeli — patrz `getAddRowDraft`. */
  onAddRowDraftChange?: (tableId: string, draft: AdditionalTableAddRowDraft) => void;
  /**
   * Firma dokumentu (F.RL, powiązania między kartotekami) — przekazywana do
   * `RecordLinksSection` jako `companyId` (gating "Dodaj powiązanie" w trybie
   * "Wszystkie firmy" + scoping pickera celu). `null`/pominięte gdy dokument
   * jeszcze nie ma zapisanej firmy (nie powinno się zdarzyć — blok i tak
   * renderuje się WYŁĄCZNIE gdy `recordId` istnieje, czyli dokument już zapisany).
   */
  companyId?: string | null;
  /**
   * F22 (widoki dodatkowe) — wartości pól ext (`extValues[slug]`) dla wiązania `record_field`.
   * Ścieżka PODSTAWOWA — działa wszędzie, w tym w trybie create (na żywo z formularza, przed
   * zapisem rekordu). Pominięte → widoki na tej stronie widzą pusty `recordValues` z tego
   * źródła (nadal działają wiązania `context.*`/`constant`).
   */
  extValues?: Record<string, unknown>;
  /**
   * F22 — wartości pól CORE dokumentu (slug→wartość), budowane przez callera z
   * `buildCoreFieldRecordValues` (`components/kartoteki/engine/engine-config.ts`) na bazie
   * `KartotekaEngineConfig.fields` tej klasy dokumentu (wszystkie 7 stron, także
   * komercyjne/wewnętrzne przez `buildDocumentEngineConfig`). Razem z `extValues` tworzy mapę
   * „nagłówek + pola dodatkowe" dla widoków dodatkowych (`record_field`) i tabel dodatkowych
   * (`parent_field`).
   */
  coreRecordValues?: Record<string, unknown>;
  /**
   * Klasa dokumentu strony — klucz wyboru pola rodzica w wiązaniu `parent_field` parametrów
   * słowników w polach wierszy tabel dodatkowych. Razem z mapą „nagłówek + pola dodatkowe"
   * (`extValues` + `coreRecordValues`) trafia do `AdditionalTableTab`; bez niej tabele nie
   * wysyłają wartości rodzica.
   */
  parentKey?: DocumentClass;
  /**
   * F17 Commit B — czy moduł Procesy jest aktywny dla tenanta (gating klocka
   * buildera `process_view`, mirror gatingu bannera `ProcessApprovalBanner` na
   * tych samych 7 stronach — patrz `processesActive` w każdej z nich). Pominięte
   * = traktowane jako `false` (brak treści), bo brak modułu jest bezpiecznym
   * domyślnym zachowaniem — mirror `editable`/`buffered` powyżej.
   */
  processesActive?: boolean;
  /**
   * Bramka przed otwarciem okna decyzji (formularz w edycji: najpierw zapis, potem decyzja) —
   * ta sama, którą strona przekazuje `ProcessApprovalBanner`. Używana przez klocek `process_view`
   * z włączonym „Pokaż przyciski decyzji” oraz przez klocek `process_action_button` („Przycisk
   * akceptacji”); pominięta = okno otwiera się od razu.
   */
  beforeDecision?: DecisionGate;
  /**
   * Typ dokumentu bieżącego rekordu — ta sama wartość, którą strona przekazuje
   * `ProcessApprovalBanner` (`documentTypeId`). Klocek `process_action_button` podaje go dalej do
   * „Wyślij do akceptacji” (wybór procesu po typie dokumentu); pominięty/`null` = rekord bez typu.
   */
  documentTypeId?: string | null;
}

/**
 * Buduje mapę contextRenderers dla zakładek tabel dodatkowych.
 *
 * @param recordId  - id rekordu dokumentu (documents.id); null/undefined gdy tryb create.
 * @param options   - { buffered, onDeltasChange, getBufferedValue } — opcjonalne.
 *
 * Zasada: gdy brak tableSlug → zwraca null (guard). Gdy brak recordId (tryb
 * create) — renderuje zakładkę TYLKO w trybie buforowanym (`options.buffered`),
 * bo tylko wtedy `AdditionalTableTab` wie, że nie ma czego pobrać z serwera i
 * pozwala dodawać wiersze lokalnie (zgłaszane przez `onDeltasChange`). Bez
 * buforowania brak recordId oznacza brak rekordu-rodzica do zapisu HTTP →
 * zwraca null jak dotąd.
 *
 * Uwaga: `tableId` (potrzebny do `getBufferedValue`) NIE jest znany tutaj —
 * `AdditionalTableTab` sam go rozwiązuje z `tableSlug` przez zapytanie o
 * listę tabel. Dlatego przekazujemy `getBufferedValue` DALEJ do
 * `AdditionalTableTab`, które woła go z rozwiązanym `tableId` przy każdym
 * renderze (patrz `bufferedValue` prop tego komponentu).
 */
export function buildDocumentAdditionalRenderers(
  recordId: string | null | undefined,
  options?: BuildDocumentAdditionalRenderersOptions,
): Record<string, (props: { element: LayoutElement }) => ReactNode> {
  const editable = options?.editable ?? true;
  const buffered = options?.buffered ?? false;
  const companyId = options?.companyId ?? null;
  const processesActive = options?.processesActive ?? false;
  // F22 — scalone recordValues dla widoków dodatkowych (record_field binding) ORAZ wartości
  // rodzica dla tabel dodatkowych (parent_field binding). Kolejność nie
  // ma znaczenia w praktyce (slugi ext vs core nie powinny kolidować), core na końcu na wypadek.
  const additionalViewRecordValues = buildAdditionalViewRecordValues(
    options?.extValues,
    options?.coreRecordValues,
  );

  function renderAdditionalTab({ element }: { element: LayoutElement }): ReactNode {
    const tableSlug =
      typeof element?.settings?.tableSlug === 'string' ? element.settings.tableSlug : '';
    if (!tableSlug) return null;
    if (!recordId && !buffered) return null;
    // `col-span-full`: tabela dodatkowa zajmuje całą szerokość sekcji — tak samo jak w
    // podglądzie kreatora formatki; bez tego trafiłaby w jedną komórkę dwukolumnowej siatki.
    return (
      <div className="col-span-full">
        <AdditionalTableTab
          tableSlug={tableSlug}
          parentId={recordId}
          editable={editable}
          buffered={buffered}
          getBufferedValue={options?.getBufferedValue}
          onDeltasChange={options?.onDeltasChange}
          getAddRowDraft={options?.getAddRowDraft}
          onAddRowDraftChange={options?.onAddRowDraftChange}
          parentValues={additionalViewRecordValues}
          parentKey={options?.parentKey}
          columnSettings={element.settings}
        />
      </div>
    );
  }

  // F22 — sekcja read-only zasilana zapytaniem SQL (widok dodatkowy). W odróżnieniu od
  // `renderAdditionalTab` (tabele dodatkowe, buforowane w create) działa BEZ warunku na
  // `recordId`/`buffered` — widok nie zapisuje nic, więc renderuje się też w trybie create
  // (wartości parametrów idą z `recordValues`, nie z rekordu w bazie — ADR-0087 Decyzja 5).
  function renderAdditionalQueryTab({ element }: { element: LayoutElement }): ReactNode {
    const viewSlug =
      typeof element?.settings?.viewSlug === 'string' ? element.settings.viewSlug : '';
    if (!viewSlug) return null;
    // `col-span-full` — jak tabela dodatkowa wyżej: widok zajmuje całą szerokość sekcji.
    return (
      <div className="col-span-full">
        <AdditionalViewTab
          viewSlug={viewSlug}
          recordId={recordId}
          recordValues={additionalViewRecordValues}
          columnSettings={element.settings}
        />
      </div>
    );
  }

  return {
    additional_view: renderAdditionalTab,
    // satellite_view: legacy alias — identyczna implementacja
    satellite_view: renderAdditionalTab,
    additional_query_view: renderAdditionalQueryTab,
    // `element.settings` — per-placement config (allowAdd/allowUnlink/allowedLinkTypeIds/
    // allowedTargetTypes/defaultLinkTypeId) ustawiony w builderze, parsowany defensywnie.
    record_links_view: ({ element }) =>
      recordId ? (
        <RecordLinksSection
          ownerType="document"
          ownerId={recordId}
          companyId={companyId}
          config={parseRecordLinksElementConfig(element.settings)}
        />
      ) : null,
    // Klocki „Proces akceptacji" (`process_view`) i „Przycisk akceptacji" (`process_action_button`):
    // gating jak `ProcessApprovalBanner` na stronach dokumentów (recordId zapisany + moduł Procesy
    // aktywny). `moduleCode` stały — wszystkie klasy dokumentów dzielą jeden kod procesu
    // `crm_core.documents`. Wspólny kształt z kartoteką Zadania (`buildProcessLayoutRenderers`).
    ...buildProcessLayoutRenderers({
      moduleCode: DOCUMENTS_MODULE_CODE,
      recordId,
      processesActive,
      typeId: options?.documentTypeId,
      beforeDecision: options?.beforeDecision,
    }),
  };
}
